import React from 'react';
import { Box, Container, Typography, Card, CardContent, Chip, LinearProgress, Grid, Tabs, Tab } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`quest-tabpanel-${index}`}
      aria-labelledby={`quest-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Quests: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Mock data for quests
  const dailyQuests = [
    { id: 1, title: 'Water 3 plants', progress: 1, total: 3, xp: 20, icon: '💧' },
    { id: 2, title: 'Complete a harvest', progress: 0, total: 1, xp: 30, icon: '🌾' },
    { id: 3, title: 'Take a plant photo', progress: 0, total: 1, xp: 15, icon: '📸' },
  ];

  const friendQuests = [
    { id: 1, title: 'Visit a friend\'s garden', progress: 0, total: 1, xp: 25, icon: '🏡' },
    { id: 2, title: 'Share a harvest', progress: 0, total: 1, xp: 40, icon: '🎁' },
  ];

  const QuestCard = ({ quest }: { quest: any }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h4">{quest.icon}</Typography>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {quest.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {quest.progress}/{quest.total} completed
              </Typography>
            </Box>
          </Box>
          <Chip label={`+${quest.xp} XP`} color="primary" size="small" />
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={(quest.progress / quest.total) * 100} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 10 }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', pt: 3, pb: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            💎 Treasure Chest
          </Typography>
          
          {/* Streak Counter */}
          <Box display="flex" gap={3} mt={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <LocalFireDepartmentIcon />
              <Typography variant="h6">{user?.gamification?.streak || 0}</Typography>
              <Typography variant="body2">day streak</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <EmojiEventsIcon />
              <Typography variant="h6">{user?.gamification?.xp || 0}</Typography>
              <Typography variant="body2">total XP</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="quest tabs">
          <Tab label="Daily" />
          <Tab label="Friends" />
          <Tab label="Achievements" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Today's Quests
          </Typography>
          {dailyQuests.map(quest => (
            <QuestCard key={quest.id} quest={quest} />
          ))}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Friend Quests
          </Typography>
          {friendQuests.map(quest => (
            <QuestCard key={quest.id} quest={quest} />
          ))}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Achievements
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3">🌱</Typography>
                <Typography variant="body2">First Plant</Typography>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3">🏆</Typography>
                <Typography variant="body2">7 Day Streak</Typography>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ textAlign: 'center', p: 2, opacity: 0.5 }}>
                <Typography variant="h3">🌳</Typography>
                <Typography variant="body2">Master Gardener</Typography>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Container>
    </Box>
  );
};

export default Quests;